import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ICategory } from 'core/types/category.interface';

@Component({
  selector: 'app-category-carousel',
  imports: [TranslatePipe],
  templateUrl: './category-carousel.component.html',
  styleUrl: './category-carousel.component.scss',
  host: { '[class.fitted]': 'categoriesFit()' }
})
export class CategoryCarouselComponent {
  readonly scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly categories = input.required<ICategory[]>();
  readonly fitCategories = input(false);
  readonly selectedCategoryId = input<number | null>(null);
  readonly categorySelected = output<number | null>();
  readonly availableWidth = signal(0);
  readonly categoriesFit = computed(
    () =>
      this.fitCategories() &&
      this.availableWidth() >= (this.categories().length + 1) * 112 + this.categories().length * 16 + 16
  );

  constructor() {
    afterNextRender(() => {
      const observer = new ResizeObserver(entries => this.availableWidth.set(entries[0].contentRect.width));
      observer.observe(this.host.nativeElement);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  selectCategory(categoryId: number | null): void {
    this.categorySelected.emit(categoryId);
  }

  scrollNext(): void {
    const scrollContainerElement = this.scrollContainer()?.nativeElement;
    if (!scrollContainerElement) {
      return;
    }

    const itemWidth: number = scrollContainerElement.firstElementChild
      ? (scrollContainerElement.firstElementChild as HTMLElement).offsetWidth
      : 0;
    const maxScrollLeft: number = scrollContainerElement.scrollWidth - scrollContainerElement.clientWidth;
    const newScrollPosition = Math.min(scrollContainerElement.scrollLeft + itemWidth, maxScrollLeft);

    scrollContainerElement.scrollTo({ left: newScrollPosition, behavior: 'smooth' });
  }

  scrollPrev(): void {
    const scrollContainerElement = this.scrollContainer()?.nativeElement;
    if (!scrollContainerElement) {
      return;
    }

    const itemWidth: number = scrollContainerElement.firstElementChild
      ? (scrollContainerElement.firstElementChild as HTMLElement).offsetWidth
      : 0;
    const newScrollPosition = Math.max(scrollContainerElement.scrollLeft - itemWidth, 0);

    scrollContainerElement.scrollTo({ left: newScrollPosition, behavior: 'smooth' });
  }
}
